import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex items-center my-2" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "h-px mx-2 bg-gray-100 dark:bg-gray-700 flex-grow" }, null, -1)
const _hoisted_3 = ["innerHTML"]

import { computed, ref, watchEffect } from 'vue';
import TokenInput from '@/components/inputs/TokenInput/TokenInput.vue';
import { bnum } from '@/lib/utils';
import useNumbers from '@/composables/useNumbers';
import useTokens from '@/composables/useTokens';
import { UseTrading } from '@/composables/trade/useTrading';
import TradePairToggle from '@/components/cards/TradeCard/TradePairToggle.vue';
import { GqlLge } from '@/embr/services/embr/embr-types';
import LgeTokenInput from '@/embr/lbp/components/TradeCard/LgeTokenInput.vue';

/**
 * TYPES
 */
type Props = {
  tokenInAmount: string;
  tokenInAddress: string;
  tokenOutAmount: string;
  tokenOutAddress: string;
  exactIn: boolean;
  priceImpact?: number;
  effectivePriceMessage?: UseTrading['effectivePriceMessage'];
  tradeLoading?: boolean;
  lge: GqlLge;
};

/**
 * PROPS & EMITS
 */

export default _defineComponent({
  props: {
    tokenInAmount: { type: String, required: true },
    tokenInAddress: { type: String, required: true },
    tokenOutAmount: { type: String, required: true },
    tokenOutAddress: { type: String, required: true },
    exactIn: { type: Boolean, required: true },
    priceImpact: { type: Number, required: false },
    effectivePriceMessage: { type: null, required: false },
    tradeLoading: { type: Boolean, required: false },
    lge: { type: null, required: true }
  } as unknown as undefined,
  emits: ["update:tokenInAmount", "update:tokenInAddress", "update:tokenOutAmount", "update:tokenOutAddress", "update:exactIn", "amountChange"] as unknown as undefined,
  setup(__props: {
  tokenInAmount: string;
  tokenInAddress: string;
  tokenOutAmount: string;
  tokenOutAddress: string;
  exactIn: boolean;
  priceImpact?: number;
  effectivePriceMessage?: UseTrading['effectivePriceMessage'];
  tradeLoading?: boolean;
  lge: GqlLge;
}, { emit }: { emit: ({
  (e: 'update:tokenInAmount', value: string): void;
  (e: 'update:tokenInAddress', value: string): void;
  (e: 'update:tokenOutAmount', value: string): void;
  (e: 'update:tokenOutAddress', value: string): void;
  (e: 'update:exactIn', value: boolean): void;
  (e: 'amountChange'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props




/**
 * COMPOSABLES
 */
const { fNum } = useNumbers();
const { getToken } = useTokens();

/**
 * STATE
 */
const _tokenInAmount = ref<string>('');
const _tokenInAddress = ref<string>('');
const _tokenOutAmount = ref<string>('');
const _tokenOutAddress = ref<string>('');

const isInRate = ref<boolean>(true);

/**
 * COMPUTED
 */
const missingToken = computed(
  () => !_tokenInAddress.value || !_tokenOutAddress.value
);

const missingAmount = computed(
  () => !_tokenInAmount.value || !_tokenOutAmount.value
);

const tokenIn = computed(() => getToken(_tokenInAddress.value));
const tokenOut = computed(() => getToken(_tokenOutAddress.value));

const rateLabel = computed(() => {
  if (missingToken.value || missingAmount.value) return '';

  if (props.effectivePriceMessage)
    return isInRate.value
      ? props.effectivePriceMessage.value.tokenIn
      : props.effectivePriceMessage.value.tokenOut;

  let rate, inSymbol, outSymbol;

  if (isInRate.value) {
    rate = bnum(_tokenOutAmount.value)
      .div(_tokenInAmount.value)
      .toString();
    inSymbol = tokenIn.value.symbol;
    outSymbol = tokenOut.value.symbol;
  } else {
    rate = bnum(_tokenInAmount.value)
      .div(_tokenOutAmount.value)
      .toString();
    inSymbol = tokenOut.value.symbol;
    outSymbol = tokenIn.value.symbol;
  }

  return `1 ${inSymbol} = ${fNum(rate, 'token')} ${outSymbol}`;
});

const tokenInIsLbpToken = computed(
  () =>
    props.lge.tokenContractAddress.toLowerCase() ===
    tokenIn.value.address.toLowerCase()
);

const tokenOutIsLbpToken = computed(
  () =>
    props.lge.tokenContractAddress.toLowerCase() ===
    tokenOut.value.address.toLowerCase()
);

/**
 * METHODS
 */
function handleInAmountChange(value: string): void {
  emit('update:exactIn', true);
  emit('update:tokenInAmount', value);
  emit('amountChange');
}

function handleOutAmountChange(value: string): void {
  emit('update:exactIn', false);
  emit('update:tokenOutAmount', value);
  emit('amountChange');
}

function handleTokenSwitch(): void {
  emit('update:exactIn', !props.exactIn);
  emit('update:tokenInAmount', _tokenOutAmount.value);
  emit('update:tokenInAddress', _tokenOutAddress.value);
  emit('update:tokenOutAmount', _tokenInAmount.value);
  emit('update:tokenOutAddress', _tokenInAddress.value);
  emit('amountChange');
}

/**
 * CALLBACKS
 */
watchEffect(() => {
  _tokenInAmount.value = props.tokenInAmount;
  _tokenInAddress.value = props.tokenInAddress;
  _tokenOutAmount.value = props.tokenOutAmount;
  _tokenOutAddress.value = props.tokenOutAddress;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(LgeTokenInput, {
      amount: _tokenInAmount.value,
      "onUpdate:amount": [
        _cache[0] || (_cache[0] = ($event: any) => (_tokenInAmount.value = $event)),
        handleInAmountChange
      ],
      address: _tokenInAddress.value,
      "onUpdate:address": [
        _cache[1] || (_cache[1] = ($event: any) => (_tokenInAddress.value = $event)),
        _cache[2] || (_cache[2] = ($event: any) => (emit('update:tokenInAddress', $event)))
      ],
      name: "tokenIn",
      excludedTokens: [_tokenOutAddress.value],
      disabled: __props.tradeLoading,
      fixedToken: _unref(tokenInIsLbpToken),
      lge: props.lge
    }, null, 8, ["amount", "address", "excludedTokens", "disabled", "fixedToken", "lge"]),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(TradePairToggle, { onToggle: handleTokenSwitch }),
      _hoisted_2,
      (_unref(rateLabel))
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "flex items-center text-xs text-gray-500 cursor-pointer",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (isInRate.value = !isInRate.value)),
            innerHTML: _unref(rateLabel)
          }, null, 8, _hoisted_3))
        : _createCommentVNode("", true)
    ]),
    _createVNode(LgeTokenInput, {
      amount: _tokenOutAmount.value,
      "onUpdate:amount": [
        _cache[4] || (_cache[4] = ($event: any) => (_tokenOutAmount.value = $event)),
        handleOutAmountChange
      ],
      address: _tokenOutAddress.value,
      "onUpdate:address": [
        _cache[5] || (_cache[5] = ($event: any) => (_tokenOutAddress.value = $event)),
        _cache[6] || (_cache[6] = ($event: any) => (emit('update:tokenOutAddress', $event)))
      ],
      name: "tokenOut",
      priceImpact: __props.priceImpact,
      noRules: "",
      noMax: "",
      disabled: __props.tradeLoading,
      excludedTokens: [_tokenInAddress.value],
      fixedToken: _unref(tokenOutIsLbpToken),
      lge: props.lge
    }, null, 8, ["amount", "address", "priceImpact", "disabled", "excludedTokens", "fixedToken", "lge"])
  ]))
}
}

})